/* eslint-disable @typescript-eslint/no-unsafe-call */
import {
  USER_ROLES_NOT_CHECK_EXP,
  getRootDomain,
  type ActivateResponse,
  type ApiError,
  type User,
  type UserRole,
  getCookie,
  setCookie,
  deleteCookie,
  type RawSetCookieOptions,
} from '@ahm/api-wrappers-core';
import { decodeToken } from '@ahm/common-helpers';
import { unix } from 'dayjs';
import { createStore } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { AuthSession } from '../api';
import type { SupplierProfile } from '../api/use-get-supplier-profile';
import {
  AuthStatus,
  AuthStep,
  AuthTab,
  COOKIE_NAME__ACCESS_TOKEN,
  COOKIE_NAME__EXPIRES_MAX_AGE__3M,
  COOKIE_NAME__REFRESH_TOKEN,
} from '../lib/constants';
import { logger } from './logger';

export type AuthState = {
  session: AuthSession | null;
  status: AuthStatus | null;
  isTokenExpired: boolean;
  apiError: ApiError | null;
  phoneNumber: string | null;
  activeAuthStep: AuthStep;
  authTabScreen: AuthTab;
  domain: string | null;
  accountsEndpoint: string | null;
  googleClientId: string | null;
  facebookAppId: string | null;
  facebookAppVersion: string;
  hasPassCode: boolean;
  dataActivate: ActivateResponse | null;
};

export type AuthActions = {
  setSession: (session: AuthSession) => void;
  clearSession: (isTokenExpired: boolean) => void;
  logout: (isTokenExpired?: boolean) => void;
  updateUserSession: (user: User) => void;
  updateSupplierSession: (supplier: SupplierProfile) => void;
  setApiError: (err: ApiError | null) => void;
  setPhoneNumber: (phoneNumber: string | null) => void;
  setAuthTabScreen: (tab: AuthTab) => void;
  setActiveAuthStep: (step: AuthStep) => void;
  setDomain: (domain: string) => void;
  setAccountsEndpoint: (endpoint: string) => void;
  setGoogleClientId: (clientId: string) => void;
  setHasPassCode: (passcode: boolean) => void;
  setFacebookClientConfig: ({
    appId,
    version,
  }: {
    appId: string;
    version: string;
  }) => void;
  setDataActivate: (data: ActivateResponse | null) => void;
};

export type AuthStore = AuthState & AuthActions;

export const defaultInitState: Omit<AuthState, 'domain'> = {
  session: null,
  status: null,
  isTokenExpired: false,
  apiError: null,
  phoneNumber: null,
  activeAuthStep: AuthStep.PhoneNumber,
  authTabScreen: AuthTab.SignIn,
  accountsEndpoint: null,
  googleClientId: null,
  facebookAppId: null,
  facebookAppVersion: 'v20.0',
  hasPassCode: true,
  dataActivate: null,
};

export const initAuthStore = (): AuthState => {
  const domain = getRootDomain() ?? null;

  return {
    ...defaultInitState,
    domain,
  };
};

export const createAuthStore = (initState: AuthState = initAuthStore()) => {
  return createStore<AuthStore>()(
    logger(
      devtools((set, get) => {
        const accessToken = getCookie(COOKIE_NAME__ACCESS_TOKEN);
        const refreshToken = getCookie(COOKIE_NAME__REFRESH_TOKEN);

        let session: AuthSession | null = null;

        if (accessToken) {
          session = {
            token: String(accessToken),
          };

          if (refreshToken) {
            session.refresh_token = String(refreshToken);
          }
        }

        return {
          ...initState,
          session,
          status: session
            ? AuthStatus.Authenticated
            : AuthStatus.Unauthenticated,
          setSession: (session) => {
            const decoded = decodeToken(session.token);

            const options: RawSetCookieOptions = {};

            if (
              decoded?.exp &&
              !USER_ROLES_NOT_CHECK_EXP.includes(decoded.role as UserRole)
            ) {
              options.expires = unix(decoded.exp).toDate();
            } else {
              options.maxAge = COOKIE_NAME__EXPIRES_MAX_AGE__3M * 24 * 60 * 60;
            }

            setCookie(COOKIE_NAME__ACCESS_TOKEN, session.token, options);

            if (session.refresh_token) {
              setCookie(COOKIE_NAME__REFRESH_TOKEN, session.refresh_token, {
                ...options,
                maxAge: COOKIE_NAME__EXPIRES_MAX_AGE__3M * 24 * 60 * 60,
              });
            }

            set({
              session,
              status: AuthStatus.Authenticated,
              isTokenExpired: false,
            });
          },
          clearSession: (isTokenExpired: boolean) => {
            deleteCookie(COOKIE_NAME__ACCESS_TOKEN);
            deleteCookie(COOKIE_NAME__REFRESH_TOKEN);

            set({
              session: null,
              status: AuthStatus.Unauthenticated,
              isTokenExpired,
            });
          },
          logout: (isTokenExpired = false) => {
            const accessToken = getCookie(COOKIE_NAME__ACCESS_TOKEN);
            const refreshToken = getCookie(COOKIE_NAME__REFRESH_TOKEN);

            let session: AuthSession | null = null;

            if (accessToken) {
              session = {
                token: String(accessToken),
              };

              if (refreshToken) {
                session.refresh_token = String(refreshToken);
              }
            }

            get().clearSession(isTokenExpired);
          },
          updateUserSession: (user) => {
            const currentSession = get().session;
            if (currentSession && user) {
              set({
                session: {
                  ...currentSession,
                  user: {
                    ...currentSession.user,
                    ...user,
                  },
                },
                status: AuthStatus.Authenticated,
                isTokenExpired: false,
              });
            }
          },
          updateSupplierSession: (supplier) => {
            const currentSession = get().session;
            if (currentSession && supplier) {
              set({
                session: {
                  ...currentSession,
                  supplier: {
                    ...currentSession.supplier,
                    ...supplier,
                  },
                },
                status: AuthStatus.Authenticated,
                isTokenExpired: false,
              });
            }
          },
          setApiError: (apiError) => {
            set({ apiError });
          },
          setPhoneNumber: (phoneNumber) => {
            set({ phoneNumber });
          },
          setAuthTabScreen: (tab: AuthTab) => {
            set({ authTabScreen: tab });
          },
          setActiveAuthStep: (step: AuthStep) => {
            set({ activeAuthStep: step });
          },
          setDomain: (domain) => {
            set({ domain });
          },
          setAccountsEndpoint: (endpoint) => {
            set({ accountsEndpoint: endpoint });
          },
          setGoogleClientId: (clientId) => {
            set({ googleClientId: clientId });
          },
          setFacebookClientConfig: ({ appId, version }) => {
            set({ facebookAppId: appId, facebookAppVersion: version });
          },
          hasPassCode: true,
          setHasPassCode: (passcode) => {
            set({ hasPassCode: passcode });
          },
          setDataActivate: (data) => {
            set({ dataActivate: data });
          },
        };
      }),
      'auth'
    )
  );
};
